import {
  BackgroundSoundCreateRequestDto,
  BackgroundSoundResponseDto,
  BackgroundSoundType,
  BackgroundSoundUploadUrlRequestDto,
  BackgroundSoundUpdateRequestDto,
} from '@shared/services/apiService/apiService';
import { clampPercentage } from '@features/Admin/AmbienceSettings/utils/ambienceSettings.utils';

export type SoundFormState = {
  name: string;
  type: BackgroundSoundType;
  defaultVolume: number;
  file: File | null;
};

export type FormErrors = {
  name?: string;
  file?: string;
};

export const MAX_BACKGROUND_SOUND_SIZE_BYTES = 10 * 1024 * 1024;

const ALLOWED_AUDIO_EXTENSIONS = ['mp3', 'wav', 'ogg'] as const;
const ALLOWED_AUDIO_MIME_TYPES = new Set([
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/x-wav',
  'audio/wave',
  'audio/vnd.wave',
  'audio/ogg',
  'application/ogg',
]);

export type SoundFileValidationError = 'invalid_type' | 'file_too_large';

const normalizeVolumeForForm = (value?: number | null) => {
  if (value === null || value === undefined) {
    return 50;
  }

  return clampPercentage(value <= 1 ? value * 100 : value);
};

const toRequestVolume = (value: number) => clampPercentage(value) / 100;

export const initialFormState: SoundFormState = {
  name: '',
  type: 'ambient',
  defaultVolume: 50,
  file: null,
};

export const createEditFormState = (sound: BackgroundSoundResponseDto): SoundFormState => ({
  name: sound.name,
  type: sound.type,
  defaultVolume: normalizeVolumeForForm(sound.defaultVolume),
  file: null,
});

export const getAudioFileExtension = (fileName: string) =>
  fileName.split('.').pop()?.trim().toLowerCase() ?? '';

export const getUploadMimeType = (file: File) => {
  const normalizedMimeType = file.type.trim().toLowerCase();

  if (normalizedMimeType) {
    return normalizedMimeType;
  }

  switch (getAudioFileExtension(file.name)) {
    case 'mp3':
      return 'audio/mpeg';
    case 'wav':
      return 'audio/wav';
    case 'ogg':
      return 'audio/ogg';
    default:
      return 'application/octet-stream';
  }
};

export const validateSoundFile = (file: File | null): SoundFileValidationError | null => {
  if (!file) {
    return null;
  }

  if (file.size > MAX_BACKGROUND_SOUND_SIZE_BYTES) {
    return 'file_too_large';
  }

  const extension = getAudioFileExtension(file.name);
  const mimeType = getUploadMimeType(file);

  if (!ALLOWED_AUDIO_EXTENSIONS.includes(extension as (typeof ALLOWED_AUDIO_EXTENSIONS)[number])) {
    return 'invalid_type';
  }

  if (!ALLOWED_AUDIO_MIME_TYPES.has(mimeType)) {
    return 'invalid_type';
  }

  return null;
};

const buildNormalizedPayload = (state: SoundFormState) => ({
  name: state.name.trim(),
  type: state.type,
  defaultVolume: toRequestVolume(state.defaultVolume),
});

export const getSelectedFileName = (
  form: SoundFormState,
  selectedSound: BackgroundSoundResponseDto | null,
  labels: {
    keepExistingFile: string;
    noFileSelected: string;
  },
) => {
  if (form.file) {
    return form.file.name;
  }

  if (selectedSound) {
    return labels.keepExistingFile;
  }

  return labels.noFileSelected;
};

export const buildUploadUrlPayload = (
  type: BackgroundSoundType,
  file: File,
): BackgroundSoundUploadUrlRequestDto => ({
  type,
  mimeType: getUploadMimeType(file),
  originalFilename: file.name,
  size: file.size,
});

export const buildCreatePayload = (
  state: SoundFormState,
  uploadKey: string,
): BackgroundSoundCreateRequestDto => ({
  ...buildNormalizedPayload(state),
  uploadKey,
});

export const buildUpdatePayload = (
  state: SoundFormState,
  uploadKey?: string,
): BackgroundSoundUpdateRequestDto => ({
  name: state.name.trim(),
  defaultVolume: toRequestVolume(state.defaultVolume),
  ...(uploadKey ? { uploadKey } : {}),
});
